/**
 * Minimal, honest data-visualization primitives.
 *
 * Rules: every chart answers a question; semantic colors only; no gradients
 * on data; no chart where a number or a table is clearer.
 */

/** Run chart of measurements against spec limits (SPC). */
export function RunChart({
  values,
  lsl,
  usl,
  mean,
  unit,
}: {
  values: number[];
  lsl: number;
  usl: number;
  mean: number | null;
  unit: string;
}) {
  const W = 640;
  const H = 180;
  const PAD = 34;
  const lo = Math.min(lsl, ...values);
  const hi = Math.max(usl, ...values);
  const span = hi - lo || 1;
  const y = (v: number) => H - PAD - ((v - lo) / span) * (H - 2 * PAD);
  const x = (i: number) =>
    values.length === 1 ? W / 2 : PAD + (i / (values.length - 1)) * (W - 2 * PAD);

  const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`Run chart of ${values.length} measurements against specification limits`}
      style={{ width: '100%', display: 'block' }}
    >
      {/* spec limits */}
      <line className="chart-limit" x1={PAD} x2={W - PAD} y1={y(usl)} y2={y(usl)} />
      <line className="chart-limit" x1={PAD} x2={W - PAD} y1={y(lsl)} y2={y(lsl)} />
      <text className="chart-axis" x={W - PAD + 4} y={y(usl) + 3}>
        USL {usl}
      </text>
      <text className="chart-axis" x={W - PAD + 4} y={y(lsl) + 3}>
        LSL {lsl}
      </text>
      {mean !== null && (
        <>
          <line className="chart-mean" x1={PAD} x2={W - PAD} y1={y(mean)} y2={y(mean)} />
          <text className="chart-axis" x={4} y={y(mean) + 3}>
            x̄ {mean.toFixed(3)}
          </text>
        </>
      )}
      <path className="chart-line" d={path} />
      {values.map((v, i) => (
        <circle
          key={i}
          className={`chart-dot${v < lsl || v > usl ? ' out' : ''}`}
          cx={x(i)}
          cy={y(v)}
          r={2.6}
        >
          <title>{`${v} ${unit} — sample ${i + 1}`}</title>
        </circle>
      ))}
    </svg>
  );
}

/**
 * Confusion matrix with EXPLICIT axis semantics (Predicted × Actual) —
 * the audit required the axes to be named, not implied.
 */
export function ConfusionMatrix({
  fn,
  fp,
  tp,
  tn,
}: {
  fn: number;
  fp: number;
  tp: number;
  tn: number;
}) {
  const cell = (label: string, value: number, tone: string) => (
    <div style={{ textAlign: 'center', padding: '10px 6px' }}>
      <div style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 650 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontVariantNumeric: 'tabular-nums', color: tone, fontWeight: 680 }}>
        {value}
      </div>
    </div>
  );
  const axis = (text: string) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--ink-3)',
      }}
    >
      {text}
    </div>
  );
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '86px 1fr 1fr',
        gridTemplateRows: '28px 1fr 1fr',
        border: '1px solid var(--border)',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'var(--surface)',
      }}
    >
      <div style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }} />
      {axis('Predicted: defect')}
      <div style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
        {axis('Predicted: good')}
      </div>

      <div
        style={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          background: 'var(--surface-2)',
          borderRight: '1px solid var(--border)',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Actual: defect
      </div>
      <div style={{ borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', background: 'var(--danger-soft)' }}>
        {cell('Escaped (missed)', fn, 'var(--danger)')}
      </div>
      <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        {cell('Correct reject', tp, 'var(--ok)')}
      </div>

      <div
        style={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          background: 'var(--surface-2)',
          borderRight: '1px solid var(--border)',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Actual: good
      </div>
      <div style={{ borderRight: '1px solid var(--border)', background: 'var(--warn-soft)' }}>
        {cell('False reject', fp, 'var(--warn)')}
      </div>
      <div style={{ background: 'var(--surface)' }}>{cell('Correct pass', tn, 'var(--ok)')}</div>
    </div>
  );
}

/** Horizontal distribution bar (fleet states, label coverage). */
export function ShareBar({
  parts,
}: {
  parts: Array<{ label: string; value: number; color: string }>;
}) {
  const total = parts.reduce((a, p) => a + p.value, 0);
  return (
    <div>
      <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--inset-deep)' }}>
        {parts.map((p) =>
          p.value > 0 ? (
            <div
              key={p.label}
              style={{ width: `${(p.value / (total || 1)) * 100}%`, background: p.color, transition: 'width 0.4s' }}
              title={`${p.label}: ${p.value}`}
            />
          ) : null,
        )}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 7, flexWrap: 'wrap' }}>
        {parts.map((p) => (
          <span key={p.label} style={{ fontSize: 11.5, color: 'var(--ink-2)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, display: 'inline-block' }} />
            {p.label} <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{p.value}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}
